<template>
  <Dropdown :placement :shown auto-boundary-max-size>
    <UIButtonCount
      :disabled
      :label
      :count
      :variant
      :leading="BarsArrowDownIcon"
      v-bind="$attrs"
      fill="light"
      data-refid="sort-button"
      @click="handleToggle"
    />
    <template #popper>
      <div
        ref="popupRef"
        v-circular-tab
        class="ui-sort"
        :data-refid="dataRefId"
      >
        <draggable
          v-model="computedValue"
          v-bind="dragOptions"
          handle=".handle"
          item-key="key"
          tag="div"
          class="ui-sort__list"
        >
          <template #item="{ element, index }">
            <SortItem
              v-bind="{
                data,
                disallowDrag,
                element,
                fields,
                index,
                total,
              }"
              :container="`[data-refid='${dataRefId}']`"
              :disabled="!count && computedValue.length === 1"
              @update="handleUpdate"
              @remove="handleRemove"
            />
          </template>
        </draggable>
        <UIButton
          label="Add sort"
          size="sm"
          :leading="PlusIcon"
          :disabled="buttonDisabled"
          @click="handleAdd"
        />
      </div>
    </template>
  </Dropdown>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { Dropdown, Placement } from 'floating-vue'
import { isEqual } from 'lodash'
import draggable from 'vuedraggable'

import { DropdownListData, SortDirection } from '@types'
import { Sort } from './utils/types'

import { formatSortParams, generateRandomKey } from './utils/helpers'

import { UIButton, UIButtonCount } from '@ui/buttons'
import { BarsArrowDownIcon, PlusIcon } from '@heroicons/vue/24/outline'
import SortItem from './SortItem.vue'

type Props = {
  buttonLabel?: string
  placement?: Placement
  fields?: DropdownListData[]
  disabled?: boolean
  disallowDrag?: boolean
  unlabeled?: boolean
}

const {
  placement = 'bottom-end',
  disallowDrag,
  ...props
} = defineProps<Props>()

const modelValue = defineModel<Sort[]>()
const modelStorage = defineModel<Sort[]>('storage')

defineOptions({
  name: 'UISort',
  inheritAttrs: false,
})

const shown = ref(false)

const fields = computed(() => props.fields || [])
const uniqueId = computed(() => getCurrentInstance()?.uid.toString())
const dataRefId = computed(() => `sort-popper-${uniqueId.value}`)

const computedValue = computed({
  get() {
    const key = generateRandomKey()
    return modelStorage.value?.length
      ? modelStorage.value
      : [
        {
          key,
          field: '',
          sortDirection: SortDirection.ASC,
        },
      ]
  },
  set(value) {
    modelStorage.value = value
  },
})

const data = computed(() => {
  const selected = modelValue.value?.map(sort => sort.field)
  return fields.value.filter(
    field => !selected?.includes(field.value.toString()),
  )
})

const total = computed(() => modelStorage.value?.length || 0)

const count = computed(() => modelValue.value?.length || undefined)
const label = computed(() => {
  if (props.unlabeled) return
  return count.value ? props.buttonLabel : props.buttonLabel || 'Sort'
})
const variant = computed(() => (count.value ? 'secondary' : 'gray'))
const buttonDisabled = computed(
  () => modelStorage.value?.length === fields.value.length,
)

const dragOptions = computed(() => ({
  animation: 200,
  disabled: disallowDrag,
}))

const escapeHandler = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  shown.value = false
}

const handleToggle = () => {
  shown.value = !shown.value
}

const handleUpdate = (data: Sort, key: string | number) => {
  if (!modelStorage.value?.length) {
    modelStorage.value = [data]
    return
  }
  modelStorage.value = modelStorage.value.map((item, index) => {
    if (
      (typeof key === 'string' && item.key === key) ||
      (typeof key === 'number' && index === key)
    ) {
      return { ...item, ...data }
    } else {
      return item
    }
  })
}

const handleRemove = (key: string | number) => {
  modelStorage.value = modelStorage.value?.filter((item, index) =>
    typeof key === 'string' ? item.key !== key : index !== key,
  )
}

const handleAdd = () => {
  computedValue.value = [
    ...(computedValue.value || []),
    {
      key: generateRandomKey(),
      field: '',
      sortDirection: SortDirection.ASC,
    },
  ]
}

watch(shown, value => {
  if (value) {
    window.addEventListener('keyup', escapeHandler)
  } else {
    window.removeEventListener('keyup', escapeHandler)
  }
})

watch(
  modelStorage,
  (value, prev) => {
    if (isEqual(value, prev)) return
    modelValue.value = formatSortParams(value)
  },
  { deep: true, immediate: true },
)
</script>

<style scoped>
.ui-sort {
  @apply py-3 px-4;

  &__list {
    @apply flex flex-col;
    @apply gap-2;
    @apply mb-4;
  }
}
</style>
